// Organization choice page for property managers and owners
// Asks whether user wants to create a new organization or join an existing one

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui';
import { apiClient } from '../../services/api';

type OrganizationChoice = 'create' | 'join' | null;

export function OrganizationChoicePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [choice, setChoice] = useState<OrganizationChoice>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChoiceSelect = (selected: OrganizationChoice) => {
    setChoice(selected);
    setError(null);
  };

  const handleContinue = async () => {
    if (!choice) {
      setError('Please select an option to continue');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (choice === 'create') {
        // User wants to create organization - proceed to setup
        navigate('/onboarding/organization');
      } else {
        // User wants to join existing organization - mark as pending and go to waiting
        await apiClient.post('/onboarding/join-organization');
        await refreshUser();
        navigate('/onboarding/waiting-pm');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save selection');
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleLabel = user?.role === 'owner' ? 'Property Owner' : 'Property Manager';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-primary-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Hi {user?.name || 'there'}!
          </h1>
          <p className="mt-2 text-gray-600">
            As a {roleLabel}, how would you like to get started?
          </p>
        </div>

        {/* Choice selection */}
        <div className="card p-8">
          {error && (
            <div
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Create organization option */}
            <button
              type="button"
              onClick={() => handleChoiceSelect('create')}
              className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                choice === 'create'
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">
                    Create a New Organization
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    I'm starting fresh and want to set up my own property management organization.
                  </p>
                </div>
                {choice === 'create' && (
                  <div className="flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-primary-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>

            {/* Join organization option */}
            <button
              type="button"
              onClick={() => handleChoiceSelect('join')}
              className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                choice === 'join'
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">
                    Join an Existing Organization
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    I've been invited by another owner or manager to help manage their properties.
                  </p>
                </div>
                {choice === 'join' && (
                  <div className="flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-primary-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          </div>

          <div className="mt-8">
            <Button
              onClick={handleContinue}
              className="w-full"
              disabled={!choice || isSubmitting}
              isLoading={isSubmitting}
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrganizationChoicePage;
